import ChangePassword from 'app/pages/common/profile/components/ChangePassword'
import {rememberLoginPasswordStrength} from 'app/pages/student/dashboard/utils/weakPassword'
import clsx from 'clsx'
import {
  getTenantCustomDomain,
  isAuthGradientDomain,
  isDa1UniversalLoginHost,
  isSelectiveAiDomains,
} from 'common/HelperFunctions'
import {USER_TYPES} from 'common/constants'
import {handlePostLoginNavigation} from 'common/helpers'
import {useFormik} from 'formik'
import {useEffect, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useDispatch, useSelector} from 'react-redux'
import {Link, useLocation} from 'react-router'
import {toast} from 'react-toastify'
import {REDUX_SAVE_LOGIN_INFO} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {login, verifyLoginCredentials} from 'setup/redux/Actions/SignIn/login'
import {getLoggedInUserData} from 'setup/redux/Actions/SignIn/login'
import * as Yup from 'yup'

import {systemMode, useThemeMode} from '_metronic/partials'

import {useAiLoginError} from '../hooks/useAiLoginError'
import {useUniversalLogin} from '../hooks/useUniversalLogin'
import {armLoginTimeout} from '../loginTimeout'
import MultiFactorAuth from '../modals/MultiFactorAuth'
import {UniversalLoginSchoolPicker} from '../modals/UniversalLoginSchoolPicker'
import {AiLoginErrorAlert} from './AiLoginErrorAlert'
import {SelectiveAiGoogleLogin} from './SelectiveAiGoogleLogin'

const initialValues = {
  email: '',
  password: '',
}

const userDetailInitialValue = {
  userType: '',
  userEmail: '',
  userId: '',
  userPassword: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

/**
 * Explicitly hand the exact credentials the user just typed to the browser's
 * password manager. This overrides any similar-but-different email that may be
 * sitting in autofill/cache, so the "Save password?" prompt always offers the
 * email + password that were actually entered.
 *
 * Chrome/Edge support the Credential Management API (PasswordCredential) and get
 * a hard guarantee. Safari ignores it and relies on the form's autocomplete
 * attributes (email = "email", password = "current-password") to save the same.
 */
const saveEnteredCredentials = (email: string, password: string) => {
  try {
    const w = window as any
    if (typeof w.PasswordCredential === 'function' && navigator.credentials?.store) {
      const credential = new w.PasswordCredential({
        id: email,
        password,
        name: email,
      })
      navigator.credentials.store(credential).catch(() => {})
    }
  } catch {
    // Browser doesn't support the Credential Management API — the form's
    // autocomplete attributes handle saving instead. No action needed.
  }
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showMfaModal, setShowMfaModal] = useState(false)
  const [userDetails, setUserDetails] = useState(userDetailInitialValue)
  const [changePassword, setChangePassword] = useState<any>(null)
  const dispatch = useDispatch()
  const location = useLocation()

  const {
    isUniversalLoginHost,
    showSchoolPicker,
    schoolInstances,
    pickerLoading,
    aiLoginError: universalAiLoginError,
    emailLoginOnly: universalEmailLoginOnly,
    showEmailLoginForm: universalShowEmailLoginForm,
    submitUniversalLogin,
    handleSchoolSelect,
    closeSchoolPicker,
  } = useUniversalLogin()

  const {
    aiLoginError: tenantAiLoginError,
    emailLoginOnly: tenantEmailLoginOnly,
    showEmailLoginForm: tenantShowEmailLoginForm,
    processAiLoginError,
  } = useAiLoginError()

  const aiLoginError = universalAiLoginError ?? tenantAiLoginError
  const emailLoginOnly = universalEmailLoginOnly || tenantEmailLoginOnly

  const isSelectiveAiDomain = isSelectiveAiDomains()
  const emailLabel = isSelectiveAiDomain ? 'Email' : 'Email/Username'
  const emailPlaceholder = isSelectiveAiDomain ? 'Email' : 'Email/Username'

  const loginSchema = Yup.object().shape({
    email: isSelectiveAiDomain
      ? Yup.string()
          .email('Enter a valid email address')
          .min(3, 'Minimum 3 symbols')
          .max(50, 'Maximum 50 symbols')
          .required('Email is required')
      : Yup.string()
          .min(3, 'Minimum 3 symbols')
          .max(50, 'Maximum 50 symbols')
          .required('Email/Username is required'),
    password: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Password is required'),
  })

  const generalSettings = useSelector<any>(({mainReducer}) => mainReducer.generalSettings) as any
  const {mode} = useThemeMode()

  function onModalError(message: string) {
    setShowMfaModal(false)
    toast.error(message)
  }

  const proceedLogin = (
    email: string,
    password: string,
    setSubmitting?: (isSubmitting: boolean) => void,
    setStatus?: (status: string) => void
  ) => {
    const cancelTimeout = armLoginTimeout({setLoading, setSubmitting, setStatus})
    dispatch(
      login(
        email,
        password,
        (res: any) => {
          cancelTimeout()
          const {promptChangePassword} = res.result

          if (promptChangePassword) {
            setChangePassword(res)
            return
          }

          // Family dashboard "Weak Password Detected" prompt (mobile parity):
          // only a weak/strong flag is kept, never the password.
          rememberLoginPasswordStrength(password)

          if (isSelectiveAiDomains()) {
            // Save exactly the email + password that were entered (strict), not a
            // similar value that may be in the browser's autofill/cache.
            saveEnteredCredentials(email, password)
            dispatch(
              getLoggedInUserData(
                () => {},
                () => {}
              )
            )
          }

          setLoading(false)
          if (setSubmitting) setSubmitting(false)

          if (
            handlePostLoginNavigation(res.result, {
              path: '/dashboard',
              searchParams: {step: '0'},
            })
          ) {
            return
          }
        },
        (res: any) => {
          cancelTimeout()
          if (isSelectiveAiDomains()) {
            const outcome = processAiLoginError(
              res,
              (message) => setStatus?.(message),
              'Invalid username or password'
            )
            if (outcome !== 'default') {
              setLoading(false)
              if (setSubmitting) setSubmitting(false)
              return
            }
          }
          const message = res?.message ?? 'Invalid username or password'
          if (setStatus) setStatus(message)
          setLoading(false)
          if (setSubmitting) setSubmitting(false)
        }
      )
    )
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setSubmitting(true)
      setStatus('')

      const onLoginFailure = (res: any) => {
        setStatus(res?.message ?? 'Invalid username or password')
        setLoading(false)
        setSubmitting(false)
      }

      if (isUniversalLoginHost && !isDa1UniversalLoginHost()) {
        submitUniversalLogin(values.email, values.password, {
          setStatus,
          setSubmitting,
          onLoadingChange: setLoading,
        })
        return
      }

      setTimeout(() => {
        const cancelTimeout = armLoginTimeout({setLoading, setSubmitting, setStatus})
        dispatch(
          verifyLoginCredentials(
            values.email,
            values.password,
            (res: any) => {
              cancelTimeout()
              const {userType, enforceMfa} = res.result
              if (enforceMfa && [USER_TYPES.ADMIN].includes(userType)) {
                setUserDetails({...res.result, userPassword: values.password})
                setShowMfaModal(true)
                setLoading(false)
                setSubmitting(false)
              } else {
                proceedLogin(values.email, values.password, setSubmitting, setStatus)
              }
            },
            (res: any) => {
              cancelTimeout()
              onLoginFailure(res)
            }
          )
        )
      }, 1000)
    },
  })

  const handleSignInWithEmail = (email?: string) => {
    if (universalAiLoginError) {
      const prefilled = universalShowEmailLoginForm(email)
      if (prefilled) formik.setFieldValue('email', prefilled)
      return
    }
    const prefilled = tenantShowEmailLoginForm(email)
    if (prefilled) formik.setFieldValue('email', prefilled)
  }

  useEffect(() => {
    const state = location.state as {emailLoginOnly?: boolean; email?: string} | null
    if (state?.emailLoginOnly) {
      const prefilled = tenantShowEmailLoginForm(state.email)
      if (prefilled) {
        formik.setFieldValue('email', prefilled)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  if (changePassword) {
    return (
      <ChangePassword
        username={changePassword?.result?.user?.email || formik.values.email}
        callback={() => {
          const payload = JSON.parse(JSON.stringify(changePassword))
          payload.result.promptChangePassword = false
          dispatch({
            type: REDUX_SAVE_LOGIN_INFO,
            payload,
          })
          setChangePassword(null)
          setLoading(false)
          handlePostLoginNavigation(payload.result, {
            path: '/dashboard',
            searchParams: {step: '0'},
          })
        }}
      />
    )
  }

  const isSpecialDomain = isAuthGradientDomain() || isSelectiveAiDomains()
  // Gradient domains always paint a dark page background; other selective-AI domains
  // follow the theme. White text is only readable when the background is actually dark.
  const onDarkBg = isAuthGradientDomain() || (mode === 'system' ? systemMode : mode) === 'dark'
  const authGradientBtnStyle = isSpecialDomain
    ? {
        background: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: 10,
        padding: '12px 28px',
        fontSize: 15,
        fontWeight: 600,
      }
    : undefined

  // Dark glass inputs only on an actually-dark background; in light theme the
  // fields fall back to the theme's solid inputs.
  const authDarkInputStyle =
    isSpecialDomain && onDarkBg
      ? {
          background: 'rgba(55, 35, 35, 0.8)',
          border: 'none',
          borderRadius: 10,
          padding: '13px 16px',
          color: 'white',
          fontSize: 15,
        }
      : undefined

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-10'>
          <h1
            className={clsx(
              'mb-3',
              isSpecialDomain
                ? clsx(onDarkBg ? 'text-white' : 'text-dark', 'fw-bolder')
                : 'text-dark'
            )}
          >
            {isSpecialDomain ? 'Sign in' : 'Sign In'}
            {!isSpecialDomain && generalSettings?.tenantName && <> {generalSettings.tenantName}</>}
          </h1>
          {isSpecialDomain && (
            <div className={clsx('fs-6', onDarkBg ? 'text-white opacity-75' : 'text-gray-600')}>
              Sign in to{' '}
              <a
                href='https://da1timetable.ai'
                target='_blank'
                rel='noreferrer'
                className='text-primary text-decoration-underline'
              >
                Da1Timetable
              </a>
            </div>
          )}
        </div>
        {/* begin::Heading */}

        {aiLoginError ? (
          <AiLoginErrorAlert
            error={aiLoginError}
            isSpecialDomain={isSpecialDomain && onDarkBg}
            onSignInWithEmail={handleSignInWithEmail}
          />
        ) : formik.status ? (
          <div className='mb-8 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : null}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <label
            className={clsx(
              'form-label fs-6 fw-bolder',
              isSpecialDomain && onDarkBg ? 'text-white' : 'text-dark'
            )}
          >
            {emailLabel} <span style={{color: '#ef4444'}}>*</span>
          </label>
          <input
            tabIndex={1}
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-lg',
              !(isSpecialDomain && onDarkBg) && 'form-control-solid',
              {'is-invalid': formik.touched.email && formik.errors.email}
            )}
            style={authDarkInputStyle}
            type='email'
            name='email'
            id='email'
            placeholder={emailPlaceholder}
            autoFocus
            autoComplete='username'
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-between mt-n5'>
            <div className='d-flex flex-stack mb-2 w-100'>
              <label
                className={clsx(
                  'form-label fw-bolder fs-6 mb-0',
                  isSpecialDomain && onDarkBg ? 'text-white' : 'text-dark'
                )}
              >
                Password <span style={{color: '#ef4444'}}>*</span>
              </label>
              <Link
                to='/auth/forgot-password'
                className={clsx(
                  'link-primary fs-6 fw-bolder',
                  isSpecialDomain && 'text-decoration-underline'
                )}
                tabIndex={4}
              >
                {isSelectiveAiDomain ? 'Forgot / Set Password?' : 'Forget Password?'}
              </Link>
            </div>
          </div>
          <div className='position-relative'>
            <input
              tabIndex={2}
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              id='current-password'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-lg',
                !(isSpecialDomain && onDarkBg) && 'form-control-solid',
                formik.touched.password && formik.errors.password && 'is-invalid'
              )}
              style={{...authDarkInputStyle, paddingRight: '4.5rem'}}
              placeholder='Password'
            />
            <div
              className={clsx(
                'position-absolute end-0 top-50 translate-middle-y',
                formik.touched.password && formik.errors.password ? 'me-12' : 'me-5',
                isSpecialDomain && onDarkBg ? 'text-white' : 'text-dark'
              )}
            >
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='tooltip-show-hide-password'>
                    {showPassword ? 'Hide' : 'Show'} Password
                  </Tooltip>
                }
              >
                <i
                  className={clsx('cursor-pointer fa', {
                    'fa-eye': !showPassword,
                    'fa-eye-slash': showPassword,
                  })}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={3}
                  aria-hidden='true'
                  aria-label='Show password'
                  role='button'
                />
              </OverlayTrigger>
            </div>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Action */}
        <div className='text-center'>
          <button
            tabIndex={4}
            type='submit'
            id='kt_sign_in_submit'
            className={clsx('btn btn-lg w-100 mb-5 rounded', !isSpecialDomain && 'btn-primary')}
            style={isSpecialDomain ? authGradientBtnStyle : undefined}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              !formik.values.email?.trim() ||
              !formik.values.password
            }
          >
            {!loading && (
              <span className='indicator-label'>{isSelectiveAiDomain ? 'Login' : 'Continue'}</span>
            )}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>

        {generalSettings.admission_form_permission === 1 && (
          <div className='w-100 text-center'>
            {/* Admission settings > "Use new admission form" picks the in-portal
                /admissions module over the legacy PHP form. */}
            {generalSettings.new_admission_form === 1 ? (
              <Link
                id='kt_admission_form_redirect'
                to='/admissions'
                target='_blank'
                className='pulsingButton'
              >
                {!loading && <span className='indicator-label'>ADMISSION FORM</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </Link>
            ) : (
              <a
                id='kt_admission_form_redirect'
                href={`https://${getTenantCustomDomain()}/admission/admissionForm`}
                target='BLANK'
                className='pulsingButton'
              >
                {!loading && <span className='indicator-label'>ADMISSION FORM</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </a>
            )}
          </div>
        )}

        {/* begin::Google Login */}
        {!emailLoginOnly ? (
          <SelectiveAiGoogleLogin
            disabled={loading}
            isSpecialDomain={isSpecialDomain && onDarkBg}
          />
        ) : null}
        {/* end::Google Login */}

        {/* end::Action */}
      </form>
      {showMfaModal && (
        <MultiFactorAuth
          showModal={showMfaModal}
          closeModal={() => setShowMfaModal(false)}
          userDetails={userDetails}
          onModalError={onModalError}
          proceedLogin={proceedLogin}
          setSubmitting={formik?.setSubmitting}
          setStatus={formik?.setStatus}
        />
      )}
      <UniversalLoginSchoolPicker
        show={showSchoolPicker}
        instances={schoolInstances}
        loading={pickerLoading}
        onSelect={(instance) =>
          handleSchoolSelect(instance, {
            setStatus: formik.setStatus,
            setSubmitting: formik.setSubmitting,
            onLoadingChange: setLoading,
          })
        }
        onClose={closeSchoolPicker}
      />
    </>
  )
}
